import {inject, Inject, Injectable} from '@angular/core';
import {AccountInfo, AuthenticationResult, IPublicClientApplication} from '@azure/msal-browser';
import {Router} from '@angular/router';
import {environment} from '../../../../environments/environment';
import {FormTypeEnum} from '../../../corporate/enums/form-type.enum';

@Injectable({
  providedIn: 'root'
})

export class RedirectHandlerService {

  private router = inject(Router);
  private redirectHandled: Promise<AuthenticationResult | null> = Promise.resolve(null);

  constructor(
    @Inject('MSAL_CUSTOMERS_LOGIN') private customerMSal: IPublicClientApplication,
  ) {
    // Only race the main MSAL's handleRedirectPromise when the user actually
    // landed on a customer-auth route. Running this on every app boot caused
    // the customer PCA to clobber the main PCA's redirect handling — the URL
    // hash from a B2C sign-in could be consumed by the wrong instance, leaving
    // the main MsalGuard with no account and bouncing the user to /login-failed.
    if (this.isCustomerAuthRoute()) {
      this.redirectHandled = this.processRedirect();
    }
  }

  private isCustomerAuthRoute(): boolean {
    if (typeof window === 'undefined') return false;
    const path = window.location.pathname || '';
    return path.startsWith('/auth/');
  }

  get activeAccount(): AccountInfo {
    return this.customerMSal.getActiveAccount();
  }

  async processRedirect(): Promise<AuthenticationResult | null> {
    try {
      // ✅ Ensure the selected instance is initialized
      await this.customerMSal.initialize();

      // Only auto-route into the customer form flow when the user is already
      // on a customer-auth route; otherwise let the main app routing decide.
      if (this.activeAccount && this.isCustomerAuthRoute()) {
        this.router.navigate([`/auth/form/${this.setClientType()}`]);
        return null;
      }

      const result = await this.customerMSal.handleRedirectPromise();

      if (result?.account) {
        this.customerMSal.setActiveAccount(result.account);
        if (this.isCustomerAuthRoute()) {
          this.router.navigate([`/auth/form/${this.setClientType()}`]);
        }
      }

      return result || null;
    } catch (err) {
      console.error('[RedirectHandlerService] redirect error', err);
      return null;
    }
  }

  public async acquireTokenSilent() {
    await this.customerMSal.initialize();
    // const res = await this.customerMSal.acquireTokenSilent({
    //   account: this.customerMSal.getAllAccounts()[0],
    //   scopes: [...environment.apiConfig.customers_msal.token_scopes.write]
    // });
    // console.log(res);
    return this.customerMSal.acquireTokenSilent({
      account: this.customerMSal.getAllAccounts()[0],
      scopes: [...environment.apiConfig.customers_msal.token_scopes.write]
    });
  }

  public async waitForRedirect(): Promise<AuthenticationResult | null> {
    if (!this.activeAccount) {
      await this.customerMSal.initialize();
    }
    return this.redirectHandled;
  }

  public setClientType() {
    const currentAccAuthority = this.customerMSal.getActiveAccount().tenantId;
    const individualAuthority = environment.b2cCustomerPolicies.names.individualSignUpPolicyId;
    const businessAuthority = environment.b2cCustomerPolicies.names.businessSignUpPolicyId;

    switch (currentAccAuthority) {
      case individualAuthority:
        return FormTypeEnum.Individual;
      case businessAuthority:
        return FormTypeEnum.Business;
      default:
        return 3;
    }
  }
}
